import { useState } from 'react';
import type { CoverageEntitiesResult, RouteMetricsResult } from '../api';
import { LAYER_GROUP_LABELS, LAYERS, type LayerGroup, type LayerTab, layerCount, type LayerKey } from './layers';

function LayerRow({
  layer,
  count,
  active,
  onToggle,
}: {
  layer: (typeof LAYERS)[number];
  count: number | undefined;
  active: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <label className="ft-layer-row">
      <input type="checkbox" checked={active} onChange={onToggle} />
      <span className="ft-layer-swatch" style={{ background: layer.color }} />
      <span className="ft-layer-label">{layer.label}</span>
      <span className="ft-layer-count">{count ?? '—'}</span>
    </label>
  );
}

/**
 * Panel de checkboxes de capas con contador — mismo componente en Ficha y
 * Cobertura. `grouped` separa visualmente "Sobre el recorrido" / "Dentro
 * de la cobertura" / secundarias (gate LineOperationsPage) — Ficha sigue
 * usando la lista plana por simplicidad de su resumen compacto.
 */
export function LayerPanel({
  routeMetrics,
  coverageEntities,
  activeLayers,
  onToggle,
  className = 'ft-layer-panel',
  grouped = false,
  tabFilter,
}: {
  routeMetrics: RouteMetricsResult | null;
  coverageEntities: CoverageEntitiesResult | null;
  activeLayers: Set<LayerKey>;
  onToggle: (key: LayerKey) => void;
  className?: string;
  grouped?: boolean;
  /** Cobertura e Infraestructura vial comparten el mismo catálogo `LAYERS` pero cada una solo debe listar/tocar las capas que le pertenecen (`layer.tab`) — sin este filtro, "Mostrar todo" en una pestaña prendía capas de la otra que después quedaban invisibles pero "activas". */
  tabFilter?: LayerTab;
}): JSX.Element {
  const layers = tabFilter ? LAYERS.filter(l => l.tab === tabFilter) : LAYERS;

  if (!grouped) {
    return (
      <div className={className}>
        {layers.map(layer => (
          <LayerRow
            key={layer.key}
            layer={layer}
            count={layerCount(layer, routeMetrics, coverageEntities)}
            active={activeLayers.has(layer.key)}
            onToggle={() => onToggle(layer.key)}
          />
        ))}
      </div>
    );
  }

  const groups: LayerGroup[] = ['route', 'coverage', 'secondary'];
  return (
    <div className={className}>
      {groups.map(group => {
        const groupLayers = layers.filter(l => l.group === group);
        if (groupLayers.length === 0) return null;
        return (
          <div key={group} className="ft-layer-group">
            <p className="ft-layer-group-title">{LAYER_GROUP_LABELS[group]}</p>
            {groupLayers.map(layer => (
              <LayerRow
                key={layer.key}
                layer={layer}
                count={layerCount(layer, routeMetrics, coverageEntities)}
                active={activeLayers.has(layer.key)}
                onToggle={() => onToggle(layer.key)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** Estado de capas activas — ninguna encendida por defecto (misma regla en Ficha y Visor). `showAll`/`hideAll` aceptan un subconjunto de claves para poder scopear "Mostrar todo" a la pestaña activa (Cobertura vs Infraestructura vial comparten el mismo Set). */
export function useLayerToggle(): {
  activeLayers: Set<LayerKey>;
  toggleLayer: (key: LayerKey) => void;
  showAll: (keys?: LayerKey[]) => void;
  hideAll: (keys?: LayerKey[]) => void;
} {
  const [activeLayers, setActiveLayers] = useState<Set<LayerKey>>(new Set());

  function toggleLayer(key: LayerKey): void {
    setActiveLayers(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function showAll(keys?: LayerKey[]): void {
    if (!keys) {
      setActiveLayers(new Set(LAYERS.map(l => l.key)));
      return;
    }
    setActiveLayers(prev => new Set([...prev, ...keys]));
  }

  function hideAll(keys?: LayerKey[]): void {
    if (!keys) {
      setActiveLayers(new Set());
      return;
    }
    setActiveLayers(prev => new Set([...prev].filter(k => !keys.includes(k))));
  }

  return { activeLayers, toggleLayer, showAll, hideAll };
}
